import { authService, databaseService } from "../firebase/firebase";
import { USER_ROLES } from "../utils/constants";

const getUserRole = async (uid) => {
  const userRecord = await databaseService.read(`users/${uid}`);
  return userRecord?.role || USER_ROLES.EMPLOYEE;
};

const requireAdmin = async (currentUid) => {
  const role = await getUserRole(currentUid);
  if (role !== USER_ROLES.ADMIN) {
    throw new Error("Unauthorized: Admin access required");
  }
  return true;
};

/**
 * Employee API Service using Firebase Realtime Database
 */
export const employeeServiceApi = {
  /**
   * Create a new employee
   */
  createEmployee: async (employeeData, currentUid) => {
    await requireAdmin(currentUid);

    const authData = await authService.registerWithEmail(employeeData.email, employeeData.password);

    const employee = {
      uid: authData.uid,
      employeeId: employeeData.employeeId,
      name: employeeData.name,
      email: employeeData.email,
      phone: employeeData.phone,
      department: employeeData.department,
      designation: employeeData.designation,
      role: "employee",
      status: employeeData.status || "active",
      createdAt: Date.now(),
    };

    await databaseService.set(`employees/${authData.uid}`, employee);
    await databaseService.set(`users/${authData.uid}`, {
      uid: authData.uid,
      role: USER_ROLES.EMPLOYEE,
      email: employeeData.email,
      createdAt: Date.now(),
    });

    return { ...employee, id: authData.uid };
  },

  /**
   * Fetch all employees - Admin only
   */
  fetchEmployees: async (currentUid) => {
    await requireAdmin(currentUid);
    return databaseService.readAll("employees");
  },

  /**
   * Fetch single employee by ID - Employee can fetch self, Admin can fetch any
   */
  fetchEmployeeById: async (id, currentUid) => {
    const currentRole = await getUserRole(currentUid);
    if (currentRole !== USER_ROLES.ADMIN && id !== currentUid) {
      throw new Error("Unauthorized: You can only view your own profile");
    }
    return databaseService.read(`employees/${id}`);
  },

  /**
   * Update employee - Admin only
   */
  updateEmployee: async (id, updates, currentUid) => {
    await requireAdmin(currentUid);
    const sanitizedUpdates = { ...updates, updatedAt: Date.now() };
    delete sanitizedUpdates.id;
    delete sanitizedUpdates.uid;
    await databaseService.update(`employees/${id}`, sanitizedUpdates);
    return true;
  },

  /**
   * Delete employee - Admin only
   */
  deleteEmployee: async (id, currentUid) => {
    await requireAdmin(currentUid);
    await databaseService.delete(`employees/${id}`);
    await databaseService.delete(`users/${id}`);
    return true;
  },

  /**
   * Disable employee - Admin only
   */
  disableEmployee: async (id, currentUid) => {
    await requireAdmin(currentUid);
    await databaseService.update(`employees/${id}`, { status: "disabled", updatedAt: Date.now() });
    return id;
  },

  /**
   * Enable employee - Admin only
   */
  enableEmployee: async (id, currentUid) => {
    await requireAdmin(currentUid);
    await databaseService.update(`employees/${id}`, { status: "active", updatedAt: Date.now() });
    return id;
  },

  /**
   * Get current user's employee profile
   */
  getMyProfile: async (currentUid) => {
    return databaseService.read(`employees/${currentUid}`);
  },
};
